/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1679616000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Mar 24 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "fullstory",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to FullStory",
          "depth": 0,
          "id": "connect-to-fullstory"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Users",
          "depth": 1,
          "id": "users"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      img: "img",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li",
      code: "code"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "You've recently built a great digital experience but need to gather information to improve upon the current iteration. How can you know what works and what doesn't? With Hightouch's FullStory integration, you can sync your most recent user properties data to better analyze who is interacting with your product and how. This allows you to make product improvements driven by meaningful data and optimized to enhance retention rates."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Users"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Users in FullStory"
            }), _jsx(_components.td, {
              children: "Update"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-fullstory",
        children: "Connect to FullStory"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "FullStory"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "FullStory"
        }), " with your FullStory ", _jsx(_components.strong, {
          children: "API key"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can find your API key within your FullStory account by clicking the dropdown button on the top left of the page, and opening ", _jsx(_components.strong, {
          children: "Settings"
        }), " > ", _jsx(_components.strong, {
          children: "API Keys"
        }), " under the ", _jsx(_components.strong, {
          children: "Integrations"
        }), " section."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-fullstory-api-key.png",
          alt: "FullStory API key"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more information on FullStory API keys, refer to ", _jsx(_components.a, {
          href: "https://help.fullstory.com/hc/en-us/articles/360052021773-Managing-API-Keys",
          children: "FullStory's documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your FullStory destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull\ndata from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview\npage"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and\nthe FullStory destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "users",
        children: "Users"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to Users in FullStory."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch matches rows from your model to FullStory users by ", _jsx(_components.strong, {
          children: "User ID"
        }), ". Select the model column that contains these values."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to the following FullStory user fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Display Name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "custom-mapping",
        children: "Custom mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can sync to existing FullStory custom fields or create them via field mapping. When entering new custom fields, ensure the field is named using the pattern ", _jsx(_components.code, {
          children: "ident_type"
        }), ",\nwhere ", _jsx(_components.code, {
          children: "ident"
        }), " is a sequence of alphanumeric characters ", _jsx(_components.code, {
          children: "A-Z"
        }), ", ", _jsx(_components.code, {
          children: "a-z"
        }), ", or ", _jsx(_components.code, {
          children: "0-9"
        }), " and ", _jsx(_components.code, {
          children: "type"
        }), " is a short suffix that denotes the field type. Custom field names must start with an alphabetic character (", _jsx(_components.code, {
          children: "A-Z"
        }), " or ", _jsx(_components.code, {
          children: "a-z"
        }), ")."]
      }), "\n", _jsxs(_components.p, {
        children: ["Example acceptable field names includes ", _jsx(_components.code, {
          children: "nickname_str"
        }), ", ", _jsx(_components.code, {
          children: "PasswordResets_int"
        }), ", and ", _jsx(_components.code, {
          children: "amtAbandonedInCart_real"
        }), ".\nRefer to the FullStory ", _jsx(_components.a, {
          href: "https://help.fullstory.com/hc/en-us/articles/4446290296599-Setting-custom-API-properties",
          children: "docs"
        }), " for more details."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the customer record in FullStory with all its synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Unset some mapped fields from FullStory"
            })]
          })]
        })]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch can only clear fields of type ", _jsx(_components.code, {
            children: "_str"
          }), ", ", _jsx(_components.code, {
            children: "_real"
          }), ", and ", _jsx(_components.code, {
            children: "_int"
          }), ". Hightouch sets the\ncleared result of ", _jsx(_components.code, {
            children: "_str"
          }), " field type to an empty string (", _jsx(_components.code, {
            children: "\"\""
          }), ") and the\ncleared result of ", _jsx(_components.code, {
            children: "_real"
          }), " and ", _jsx(_components.code, {
            children: "_int"
          }), " to ", _jsx(_components.code, {
            children: "0"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
